import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert, FlatList, TouchableOpacity} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Snackbar,
  Divider,
  FAB,
  Chip,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useCouponStore} from '../../store/useCouponStore';
import {colors, spacing, borderRadius} from '../../theme';

const APPLICABILITY_LABELS = {
  FIRST_ORDER: 'First Order',
  ONCE_PER_CUSTOMER: 'Only Once',
  UNLIMITED: 'Unlimited',
  LIMITED: 'Limited',
};

const CouponListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {coupons, isLoading, fetchCoupons, deleteCoupon} =
    useCouponStore();

  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCoupons();
    setRefreshing(false);
  }, [fetchCoupons]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(
        t('common.delete'),
        t('coupon.deleteConfirm', {name: item.name}),
        [
          {text: t('common.cancel'), style: 'cancel'},
          {
            text: t('common.delete'),
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteCoupon(item.id);
              } catch (e) {
                setSnackbar(
                  e.response?.data?.error?.message || t('common.error'),
                );
              }
            },
          },
        ],
      );
    },
    [deleteCoupon, t],
  );

  const formatValue = useCallback(item => {
    if (item.type === 'PERCENTAGE') {
      return `${Number(item.value)}%`;
    }
    return `\u20B9${Number(item.value)}`;
  }, []);

  const renderItem = useCallback(
    ({item}) => {
      return (
        <TouchableOpacity
          style={styles.card}
          activeOpacity={0.7}
          onPress={() => navigation.navigate('CouponForm', {coupon: item})}>
          <View style={styles.cardRow}>
            <View style={styles.valueContainer}>
              <Text style={styles.valueText}>{formatValue(item)}</Text>
            </View>

            <View style={styles.cardCenter}>
              <Text style={styles.codeText}>{item.code}</Text>
              <Text style={styles.nameText}>{item.name}</Text>
              {item.expiryDate && (
                <Text style={styles.detailText}>
                  {t('coupon.expires')}: {item.expiryDate}
                </Text>
              )}
              {item.minOrderValue && (
                <Text style={styles.detailText}>
                  {t('coupon.minOrder')}: \u20B9{Number(item.minOrderValue)}
                </Text>
              )}
              {item.maxDiscountValue && item.type === 'PERCENTAGE' && (
                <Text style={styles.detailText}>
                  {t('coupon.maxDiscount')}: \u20B9{Number(item.maxDiscountValue)}
                </Text>
              )}
              <View style={styles.chipRow}>
                <Chip
                  style={[
                    styles.chip,
                    item.availability === 'PUBLIC'
                      ? styles.chipPublic
                      : styles.chipPrivate,
                  ]}>
                  <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{item.availability === 'PUBLIC'
                    ? t('coupon.public')
                    : t('coupon.private')}</Text>
                </Chip>
                <Chip style={[styles.chip, styles.chipApplicability]}>
                  <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{APPLICABILITY_LABELS[item.applicability] || item.applicability}</Text>
                </Chip>
              </View>
            </View>

            <View style={styles.cardActions}>
              <Text style={styles.chevron}>{'\u203A'}</Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    },
    [formatValue, navigation, t],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View>
        <View style={styles.descriptionRow}>
          <Text style={styles.descriptionText}>
            {t('coupon.description')}
          </Text>
        </View>
        <Divider />
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => <Text style={styles.emptyText}>{t('common.noData')}</Text>,
    [t],
  );

  if (isLoading && coupons.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={t('coupon.title')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <FlatList
        data={coupons}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        contentContainerStyle={styles.listContent}
      />

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => navigation.navigate('CouponForm')}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  listContent: {
    paddingBottom: 80,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },
  card: {
    backgroundColor: colors.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  valueContainer: {
    width: 72,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  valueText: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.primary,
  },
  cardCenter: {
    flex: 1,
  },
  codeText: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  nameText: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 2,
  },
  detailText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: spacing.xs,
    gap: spacing.xs,
  },
  chip: {
    height: 24,
    paddingVertical: 0,
  },
  chipText: {
    fontSize: 11,
    lineHeight: 14,
  },
  chipPublic: {
    backgroundColor: '#E8F5E9',
  },
  chipPrivate: {
    backgroundColor: '#FFF3E0',
  },
  chipApplicability: {
    backgroundColor: '#E3F2FD',
  },
  cardActions: {
    paddingLeft: spacing.sm,
    justifyContent: 'center',
  },
  chevron: {
    fontSize: 28,
    color: colors.textSecondary,
  },
});

export default CouponListScreen;
